import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, SchemaForm, SchemaIcon } from './common';
import Picker from './nucleus/Picker';
import { ShieldCheck } from 'lucide-react';
import api from '../services/api';
import { schemaLabel } from '../utils/schemaUtils';
import './CreateNodeModal.css';

const FALLBACK_DEFAULT_SCHEMA_ID = '__default__';
const FALLBACK_DEFAULT_SCHEMA = {
    id: FALLBACK_DEFAULT_SCHEMA_ID,
    schema_name: 'paradigm:Default',
    name: 'Default',
    description: 'System default schema with a single content field',
    category: 'system',
    schema_metadata: { icon: 'file-text' },
    json_schema: {
        type: 'object',
        properties: {
            content: {
                type: 'string',
                title: 'Content',
                description: 'Primary content or description',
            },
        },
        required: [],
    },
};

const CreateNodeModal = ({ onCreateNode, onCancel, availableSchemas: propSchemas = [] }) => {
    const [formData, setFormData] = useState({
        title: '',
    });

    const [schemas, setSchemas] = useState(() => {
        const hasDefault = propSchemas?.some(s => s.schema_name === 'paradigm:Default');
        return hasDefault ? propSchemas : [...(propSchemas || []), FALLBACK_DEFAULT_SCHEMA];
    });

    const ensureDefaultSchema = (list) => {
        const hasDefault = list?.some(s => s.schema_name === 'paradigm:Default');
        if (hasDefault) return list;
        return [...(list || []), FALLBACK_DEFAULT_SCHEMA];
    };

    // If no schemas were passed by the parent, fetch them directly
    useEffect(() => {
        if (propSchemas.length > 0) {
            setSchemas(ensureDefaultSchema(propSchemas));
            return;
        }
        api.getSchemas({ include_json_schema: 'false', limit: 200 }).then((schemas) => ({ schemas }))
            .then(r => setSchemas(ensureDefaultSchema(r.schemas || [])))
            .catch(() => setSchemas(ensureDefaultSchema([])));
    }, [propSchemas.length]);

    // Find the Default schema and auto-select it
    const defaultSchema = schemas.find(s => s.schema_name === 'paradigm:Default');
    const schemaItems = useMemo(() => {
        const list = Array.isArray(schemas) ? schemas : [];
        const realDefault = list.find(s => s.schema_name === 'paradigm:Default');
        if (realDefault) {
            return [realDefault, ...list.filter(s => s.id !== realDefault.id)];
        }
        return [FALLBACK_DEFAULT_SCHEMA, ...list.filter(s => s.id !== FALLBACK_DEFAULT_SCHEMA_ID)];
    }, [schemas]);
    const [selectedSchemaId, setSelectedSchemaId] = useState('');
    const [schemaMetadata, setSchemaMetadata] = useState({});
    const [fullSchema, setFullSchema] = useState(null);
    const [parentSchemaData, setParentSchemaData] = useState(null);
    const [schemaLoading, setSchemaLoading] = useState(false);

    // Auto-select Default schema on mount
    useEffect(() => {
        if (!selectedSchemaId && defaultSchema) {
            setSelectedSchemaId(defaultSchema.id);
        }
    }, [defaultSchema]);

    // If we were using the fallback and a real Default schema is available, switch to it.
    useEffect(() => {
        if (selectedSchemaId === FALLBACK_DEFAULT_SCHEMA_ID && defaultSchema?.id && defaultSchema.id !== FALLBACK_DEFAULT_SCHEMA_ID) {
            setSelectedSchemaId(defaultSchema.id);
        }
    }, [defaultSchema?.id, selectedSchemaId]);

    // Lazy-load full schema (with json_schema) when a schema is selected
    // If the schema extends a parent, also fetch the parent schema directly
    useEffect(() => {
        if (!selectedSchemaId) {
            setFullSchema(null);
            setParentSchemaData(null);
            setSchemaLoading(false);
            return;
        }
        if (selectedSchemaId === FALLBACK_DEFAULT_SCHEMA_ID) {
            setFullSchema(FALLBACK_DEFAULT_SCHEMA);
            setParentSchemaData(null);
            setSchemaLoading(false);
            return;
        }
        let cancelled = false;
        setSchemaLoading(true);
        api.getSchema(`${selectedSchemaId}?include_json_schema=true`)
            .then(s => {
                if (cancelled) return;
                setFullSchema(s);
                // If schema extends a parent, fetch the parent's fields
                if (s.parent_schema?.id) {
                    return api.request(`/schemas/${s.parent_schema.id}?include_json_schema=true&resolve=true`)
                        .then(parent => { if (!cancelled) setParentSchemaData(parent); })
                        .catch(() => { if (!cancelled) setParentSchemaData(null); });
                } else {
                    setParentSchemaData(null);
                }
            })
            .catch(() => { if (!cancelled) { setFullSchema(null); setParentSchemaData(null); } })
            .finally(() => { if (!cancelled) setSchemaLoading(false); });
        return () => { cancelled = true; };
    }, [selectedSchemaId]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.title || !formData.title.trim()) {
            alert('Title is required');
            return;
        }

        onCreateNode({
            title: formData.title,
            schema_id: selectedSchemaId && selectedSchemaId !== FALLBACK_DEFAULT_SCHEMA_ID ? selectedSchemaId : undefined,
            value_json: schemaMetadata,
        });
    };

    return (
        <Modal isOpen={true} onClose={onCancel} title="Create New Node" size="lg">
            <form onSubmit={handleSubmit} className="create-node-form">
                {/* Title */}
                <div className="form-group">
                    <label>Title *</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleChange('title', e.target.value)}
                        placeholder="Short title"
                        maxLength={120}
                        required
                        autoFocus
                    />
                </div>

                {/* Schema Selector */}
                {schemas.length > 0 && (
                    <div className="form-group">
                        <label>Schema</label>
                        <Picker
                            fill
                            searchable
                            label="Schema"
                            value={selectedSchemaId || ''}
                            onChange={(id) => {
                                setSelectedSchemaId(id || '');
                                setSchemaMetadata({});
                            }}
                            options={[
                                { value: '', label: 'None' },
                                ...schemaItems.map(s => ({
                                    value: s.id,
                                    label: schemaLabel(s.schema_name),
                                    group: s.category || 'Other',
                                    icon: (
                                        <>
                                            <SchemaIcon iconName={s.schema_metadata?.icon} size={14} />
                                            {s.is_system && <ShieldCheck size={12} className="schema-validated-icon" title="Validated Schema" />}
                                        </>
                                    ),
                                })),
                            ]}
                        />
                    </div>
                )}

                {/* Schema Fields */}
                {selectedSchemaId && (
                    <div className="form-group">
                        {schemaLoading ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '8px 0' }}>Loading schema fields...</div>
                        ) : fullSchema?.json_schema?.properties || parentSchemaData?.json_schema?.properties ? (
                            <>
                                {parentSchemaData?.json_schema?.properties && (
                                    <SchemaForm
                                        schema={parentSchemaData.json_schema}
                                        values={schemaMetadata}
                                        onChange={(key, val) => setSchemaMetadata(prev => ({ ...prev, [key]: val }))}
                                        mode="edit"
                                        schemaName={`Inherited from ${schemaLabel(parentSchemaData.schema_name) || 'parent'}`}
                                    />
                                )}
                                {fullSchema?.json_schema?.properties && (
                                    <SchemaForm
                                        schema={fullSchema.json_schema}
                                        values={schemaMetadata}
                                        onChange={(key, val) => setSchemaMetadata(prev => ({ ...prev, [key]: val }))}
                                        mode="edit"
                                        schemaName={schemaLabel(fullSchema.schema_name) || 'Schema Fields'}
                                    />
                                )}
                            </>
                        ) : fullSchema ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>This schema has no fields to fill in.</div>
                        ) : null}
                    </div>
                )}

                {/* Footer inside form so submit works */}
                <div className="p-modal__footer create-node-form__footer">
                    <Button variant="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit">
                        Create Node
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default CreateNodeModal;
